import { isValidStepByStepUser } from '@/utils/validators';
import { createSubState } from '@/utils/vuexHelpers';
import { createApiClient } from '@/utils/apiClient';

const createStore = app => {
    const state = {
        policy: createSubState(),
        zorgverbruik: createSubState(),
        reimbursements: { ...createSubState(), selected: [], lastSelected: [] },
        suggestion: createSubState()
    };

    const getters = {
        getPolicy: ({ policy }) => policy,
        getActiveUser: ({ policy }, _getters, _rootState, rootGetters) => {
            const userindex = +rootGetters.getUserIndex;
            return policy.data?.verzekerden[userindex - 1];
        },
        getValidPolicyUsers: ({ policy }) =>
            // Filter out users without packages and children under 18
            (policy.data?.verzekerden ?? []).filter(u => isValidStepByStepUser(u)),
        getZorgverbruik: ({ zorgverbruik }) => zorgverbruik,
        getCurrentZorgverbruik: ({ zorgverbruik }, _getters, _rootState, rootGetters) =>
            zorgverbruik.data?.[+rootGetters.getUserIndex],
        getReimbursements: ({ reimbursements }) => reimbursements,
        getReimbursementsList: ({ reimbursements }) => {
            const flatReimbursements = [
                ...(reimbursements.data || []).reduce((acc, curr) => {
                    if (curr.subthemes?.length) {
                        return curr.subthemes.reduce(
                            (acc2, curr2) => [...acc2, ...curr2.reimbursements],
                            acc
                        );
                    }

                    return [...acc, ...curr.reimbursements];
                }, [])
            ];

            // Create an unique list without duplicates using native Map
            // https://stackoverflow.com/a/56768137
            return [...new Map([...flatReimbursements].map(v => [v.refTitle, v])).values()];
        },
        getReimbursementsSelected: ({ reimbursements }, getters) =>
            reimbursements.selected.map(sr =>
                getters.getReimbursementsList.find(({ refTitle }) => sr === refTitle)
            ),
        getSuggestion: ({ suggestion }) => suggestion,
        getCurrentSuggestion: ({ suggestion }, _getters, _rootState, rootGetters) =>
            suggestion.data?.[+rootGetters.getUserIndex]
    };

    const actions = {
        fetchPolicy: async ({ getters, commit }) => {
            if (getters.getPolicy.data) return;

            try {
                commit('SET_POLICY_ERROR', false);
                commit('SET_POLICY_PENDING', true);

                const appContext = app.config.globalProperties.$jss.appContext();
                const { data } = await createApiClient(appContext).get(
                    `/clientcontroller/stappenplanclient/retrievepolicy`
                );

                if (data?.policyDetails) {
                    commit('SET_POLICY_DATA', data.policyDetails);
                } else {
                    throw new Error('Invalid data');
                }
            } catch {
                commit('SET_POLICY_ERROR', true);
            } finally {
                commit('SET_POLICY_PENDING', false);
            }
        },
        fetchZorgverbruik: async ({ getters, commit, rootGetters }) => {
            const userIndex = +rootGetters.getUserIndex;

            if (!getters.getActiveUser?.isAuthorized || getters.getCurrentZorgverbruik) return;

            commit('SET_ZORGVERBRUIK_ERROR', false);
            commit('SET_ZORGVERBRUIK_PENDING', true);

            try {
                const appContext = app.config.globalProperties.$jss.appContext();
                const { data } = await createApiClient(appContext).get(
                    `/clientcontroller/stappenplanclient/zorgverbruik/${userIndex}`
                );

                if (data?.zorgverbruik) {
                    commit('SET_ZORGVERBRUIK', { index: userIndex, data: data.zorgverbruik });
                } else {
                    throw new Error('Invalid data');
                }
            } catch {
                commit('SET_ZORGVERBRUIK_ERROR', true);
            } finally {
                commit('SET_ZORGVERBRUIK_PENDING', false);
            }
        },
        fetchReimbursements: async ({ getters, commit }) => {
            if (getters.getReimbursements.data) return;

            commit('SET_REIMBURSEMENTS_ERROR', false);
            commit('SET_REIMBURSEMENTS_PENDING', true);

            try {
                const appContext = app.config.globalProperties.$jss.appContext();
                const { data } = await createApiClient(appContext).post(
                    `/clientcontroller/stappenplanclient/reimbursements`,
                    {
                        proposition: appContext?.propositie
                    }
                );

                if (data?.themes && data?.themes.length) {
                    commit('SET_REIMBURSEMENTS_DATA', data.themes);
                } else {
                    throw new Error('Invalid data');
                }
            } catch {
                commit('SET_REIMBURSEMENTS_ERROR', true);
            } finally {
                commit('SET_REIMBURSEMENTS_PENDING', false);
            }
        },
        toggleReimbursement: ({ commit, getters }, refTitle) => {
            commit(
                getters.getReimbursements.selected.includes(refTitle)
                    ? 'DELETE_REIMBURSEMENT'
                    : 'ADD_REIMBURSEMENT',
                refTitle
            );
        },
        fetchSuggestion: async ({ rootGetters, getters, commit, dispatch }) => {
            const userIndex = +rootGetters.getUserIndex;
            const reimbursements = getters.getReimbursements.selected.map(r => r.toLowerCase());

            // Prevent fetching suggestion with same reimbursement selection as before
            if (
                getters.getCurrentSuggestion &&
                JSON.stringify(reimbursements) ===
                    JSON.stringify(getters.getReimbursements.lastSelected)
            ) {
                return;
            }

            commit('SET_SUGGESTION_ERROR', false);
            commit('SET_SUGGESTION_PENDING', true);

            try {
                const appContext = app.config.globalProperties.$jss.appContext();
                const { data } = await createApiClient(appContext).post(
                    `/clientcontroller/stappenplanclient/getstepbystepplanproducts`,
                    {
                        index: userIndex,
                        proposition: appContext?.propositie,
                        desiredReimbursementRefs: reimbursements,
                        language: rootGetters['renderInfo/getTaal'] === 'en' ? 'en-US' : 'nl-NL'
                    }
                );

                if (data && data.products.length) {
                    commit('SET_SUGGESTION_DATA', { index: userIndex, data });
                    commit('SET_REIMBURSEMENTS_LAST_SELECTED', reimbursements);

                    // Preselect basic and additional products (when not set yet)
                    if (!rootGetters['users/getPakketKeuzes']) {
                        dispatch(
                            'users/setPakketSelectie',
                            {
                                basis: data.currentBasicInsurance ?? '',
                                aanvullend:
                                    data.products.find(p => p.isCurrentlyActive)
                                        ?.shortDescription ?? ''
                            },
                            { root: true }
                        );
                    }
                } else {
                    throw new Error('Invalid data');
                }
            } catch {
                commit('SET_SUGGESTION_ERROR', true);
            } finally {
                commit('SET_SUGGESTION_PENDING', false);
            }
        },
        clear: ({ commit }) => {
            commit('CLEAR');
        },
        clearSuggestionByIndex: ({ commit, getters }, index) => {
            if (getters.getSuggestion.data?.[index]) {
                commit('SET_SUGGESTION_DATA', { index, data: null });
            }
        }
    };

    const mutations = {
        SET_POLICY_DATA: (state, data) => {
            state.policy.data = data;
        },
        SET_POLICY_PENDING: (state, pending) => {
            state.policy.pending = pending;
        },
        SET_POLICY_ERROR: (state, error) => {
            state.policy.error = error;
        },
        SET_ZORGVERBRUIK: (state, { index, data }) => {
            if (state.zorgverbruik.data) {
                state.zorgverbruik.data[index] = data;
            } else {
                state.zorgverbruik.data = { [index]: data };
            }
        },
        SET_ZORGVERBRUIK_PENDING: (state, pending) => {
            state.zorgverbruik.pending = pending;
        },
        SET_ZORGVERBRUIK_ERROR: (state, error) => {
            state.zorgverbruik.error = error;
        },
        SET_REIMBURSEMENTS_DATA: (state, data) => {
            state.reimbursements.data = data;
        },
        SET_REIMBURSEMENTS_PENDING: (state, pending) => {
            state.reimbursements.pending = pending;
        },
        SET_REIMBURSEMENTS_ERROR: (state, error) => {
            state.reimbursements.error = error;
        },
        ADD_REIMBURSEMENT: (state, refTitle) => {
            state.reimbursements.selected.push(refTitle);
        },
        DELETE_REIMBURSEMENT: (state, refTitle) => {
            state.reimbursements.selected = state.reimbursements.selected.filter(
                r => r !== refTitle
            );
        },
        SET_REIMBURSEMENTS_LAST_SELECTED: (state, payload) => {
            state.reimbursements.lastSelected = payload;
        },
        SET_SUGGESTION_DATA: (state, { index, data }) => {
            if (state.suggestion.data) {
                state.suggestion.data[index] = data;
            } else {
                state.suggestion.data = { [index]: data };
            }
        },
        SET_SUGGESTION_PENDING: (state, pending) => {
            state.suggestion.pending = pending;
        },
        SET_SUGGESTION_ERROR: (state, error) => {
            state.suggestion.error = error;
        },
        CLEAR: state => {
            state.policy = createSubState();
            state.pending = false;
            state.error = false;
            state.zorgverbruik = createSubState();
            state.reimbursements = { ...createSubState(), selected: [], lastSelected: [] };
            state.suggestion = createSubState();
        }
    };

    return {
        namespaced: true,
        state,
        getters,
        actions,
        mutations
    };
};

export const stappenplanStoragePaths = [
    'stappenplan.toestemming',
    'stappenplan.policy.data',
    'stappenplan.zorgverbruik.data',
    'stappenplan.reimbursements.data',
    'stappenplan.reimbursements.selected',
    'stappenplan.reimbursements.lastSelected',
    'stappenplan.suggestion.data'
];

export default app => createStore(app);
